import {Alert, Button, Group, Modal, NumberInput, Select, Stack, TextInput} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useEffect, useMemo, useState} from 'react'

import type {
    CreateThirdPartyRentalAssetOfferingInput,
    RentalAssetType,
    ThirdPartyRentalAssetOffering,
    ThirdPartyRentalCompany,
    UpdateThirdPartyRentalAssetOfferingInput,
} from './schemas'

type ThirdPartyRentalAssetOfferingFormValues = {
    companyId: string | null
    rentalAssetTypeId: string | null
    defaultDailyRate: number | string
}

type ThirdPartyRentalAssetOfferingFormModalProps = {
    opened: boolean
    offering: ThirdPartyRentalAssetOffering | null
    companies: ThirdPartyRentalCompany[]
    rentalAssetTypes: RentalAssetType[]
    lockedRentalAssetTypeId?: string
    lockedRentalAssetTypeName?: string
    onClose: () => void
    onCreate: (
        input: CreateThirdPartyRentalAssetOfferingInput,
    ) => Promise<ThirdPartyRentalAssetOffering>
    onUpdate: (
        id: string,
        input: UpdateThirdPartyRentalAssetOfferingInput,
    ) => Promise<ThirdPartyRentalAssetOffering>
    onSaved: () => void | Promise<void>
}

export function ThirdPartyRentalAssetOfferingFormModal({
    opened,
    offering,
    companies,
    rentalAssetTypes,
    lockedRentalAssetTypeId,
    lockedRentalAssetTypeName,
    onClose,
    onCreate,
    onUpdate,
    onSaved,
}: ThirdPartyRentalAssetOfferingFormModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isEditing = offering != null

    const form = useForm<ThirdPartyRentalAssetOfferingFormValues>({
        initialValues: {
            companyId: null,
            rentalAssetTypeId: null,
            defaultDailyRate: '',
        },
        validate: {
            companyId: (value) => (isEditing || value ? null : 'Company is required'),
            rentalAssetTypeId: (value) =>
                isEditing || value ? null : 'Rental asset type is required',
        },
    })

    useEffect(() => {
        if (!opened) {
            return
        }
        form.setValues({
            companyId: offering?.companyId ?? (companies.length >= 1 ? companies[0]!.id : null),
            rentalAssetTypeId:
                offering?.rentalAssetTypeId ??
                lockedRentalAssetTypeId ??
                (rentalAssetTypes.length >= 1 ? rentalAssetTypes[0]!.id : null),
            defaultDailyRate: offering?.defaultDailyRate ?? '',
        })
        form.clearErrors()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset modal fields when opened
    }, [opened, offering, companies, rentalAssetTypes, lockedRentalAssetTypeId])

    const companyOptions = useMemo(
        () => companies.map((company) => ({value: company.id, label: company.name})),
        [companies],
    )

    const rentalAssetTypeOptions = useMemo(
        () => rentalAssetTypes.map((type) => ({value: type.id, label: type.name})),
        [rentalAssetTypes],
    )

    function handleClose() {
        form.reset()
        onClose()
    }

    async function handleSubmit(values: ThirdPartyRentalAssetOfferingFormValues) {
        setIsSubmitting(true)
        try {
            const defaultDailyRate =
                values.defaultDailyRate === '' || values.defaultDailyRate == null
                    ? null
                    : Number(values.defaultDailyRate)

            if (isEditing && offering) {
                await onUpdate(offering.id, {defaultDailyRate})
            } else if (values.companyId) {
                const rentalAssetTypeId = lockedRentalAssetTypeId ?? values.rentalAssetTypeId
                if (rentalAssetTypeId) {
                    await onCreate({
                        companyId: values.companyId,
                        rentalAssetTypeId,
                        defaultDailyRate,
                    })
                }
            }
            await onSaved()
            form.reset()
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={isEditing ? 'Edit offering' : 'Create offering'}
            centered
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    {isEditing ? (
                        <>
                            <TextInput label="Company" value={offering.companyName} readOnly />
                            <TextInput
                                label="Asset type"
                                value={offering.rentalAssetTypeName}
                                readOnly
                            />
                        </>
                    ) : (
                        <>
                            {companies.length === 0 && (
                                <Alert color="yellow" title="No companies yet">
                                    Add a third-party rental company before creating an offering.
                                </Alert>
                            )}
                            {rentalAssetTypes.length === 0 && (
                                <Alert color="yellow" title="No asset types yet">
                                    Add a rental asset type before creating an offering.
                                </Alert>
                            )}
                            <Select
                                label="Company"
                                placeholder="Select a company"
                                data={companyOptions}
                                searchable
                                withAsterisk
                                {...form.getInputProps('companyId')}
                            />
                            {lockedRentalAssetTypeId ? (
                                <TextInput
                                    label="Asset type"
                                    value={lockedRentalAssetTypeName ?? lockedRentalAssetTypeId}
                                    readOnly
                                />
                            ) : (
                                <Select
                                    label="Asset type"
                                    placeholder="Select an asset type"
                                    data={rentalAssetTypeOptions}
                                    searchable
                                    withAsterisk
                                    {...form.getInputProps('rentalAssetTypeId')}
                                />
                            )}
                        </>
                    )}

                    <NumberInput
                        label="Default daily rate ($)"
                        description="Optional reference rate"
                        min={0}
                        decimalScale={2}
                        prefix="$"
                        {...form.getInputProps('defaultDailyRate')}
                    />

                    <Group justify="flex-end">
                        <Button type="button" variant="default" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            loading={isSubmitting}
                            disabled={
                                !isEditing &&
                                (companies.length === 0 || rentalAssetTypes.length === 0)
                            }
                        >
                            {isEditing ? 'Save changes' : 'Create offering'}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    )
}
